import {Component,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EtatOperation,ListOpeCarte} from '@domain/opecarte/list-opecarte';
import {OpeCarteListItemComponent} from "./opecarte-list-item/opecarte-list-item.component";
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {environment} from '@environments/environment';
import {Sorting} from '@domain/common/list-view/sorting';
import {ActivatedRoute} from "@angular/router";
import {ListViewComponent} from "@share/component/list-view/list-view.component";
import {BehaviorSubject,Subscription} from "rxjs";
import {finalize,first} from "rxjs/operators";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {OpeCarteService} from "@components/opecarte/opecarte.service";
import {PleaseWaitService} from "@share/component/please-wait/please-wait.service";
import {ToastrService} from "ngx-toastr";
import {TypeCodeErreur} from '@domain/common/http/result';
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {User} from '@domain/user/user';
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";

@Component({
    selector: 'opecarte-list',
    host: {'data-test-id': 'opecarte-list'},
    templateUrl: './opecarte-list.component.html'
})
export class OpeCarteListComponent  implements OnInit,OnDestroy {
    /** Élements de la liste des opérations carte */
    items: ListOpeCarte[] = [];

    /** Liste des opérations carte d'un utilisateur */
    listeOpeCarte: ListView<ListOpeCarte, OpeCarteListItemComponent>;

    /** Rafraichissement de la liste nécessaire */
    isRefreshNeeded: boolean = false;

    /** Composant de liste */
    @ViewChild('listeViewOpeCarte') listeViewOpeCarte: ListViewComponent<any,any>;

    /** Souscription */
    souscription: Subscription;

    /** Liste des actions */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Id de l'utilisateur pour qui on affiche la liste */
    userId: number;

    /**
     * Constructeur
     */
    constructor(
        private translateService: TranslateService,
        private activatedRoute: ActivatedRoute,
        private opeCarteService: OpeCarteService,
        private toastrService: ToastrService,
        private pleaseWaitService: PleaseWaitService,
        private store: Store<AppState>
    ) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération des paramètres de navigation
        this.souscription = this.activatedRoute.params.subscribe(params => {
            //Récupération de l'id de l'utilisateur
            this.userId = params?.id;

            //Chercher l'utilisateur connecté si aucun id n'est fourni
            if (this.userId === undefined) {
                this.store.select(state => state.session?.user).pipe(filterFirstNotNull()).subscribe(user => {
                    this.userId = user.idUser;
                });
            }

            //Si un rafraichissement est nécessaire
            if (this.isRefreshNeeded) {
                //Mise à jour d'URL de la liste
                this.listeOpeCarte.uri = `${environment.baseUrl}/controller/OpeCarte/listeCarte/${ this.userId }`;

                //Rechargement de la liste
                this.listeViewOpeCarte.refreshData();
            } else {
                //Initialisation de la liste des items
                this.listeOpeCarte = new ListView<ListOpeCarte,OpeCarteListItemComponent>({
                    uri: `${environment.baseUrl}/controller/OpeCarte/listeCarte${ params?.id ? ('/' + params.id) : '' }`,
                    title: this.translateService.instant('opeCarte.liste.title'),
                    component: OpeCarteListItemComponent,
                    defaultOrder: '-dateVente,mccLibelle',
                    mapResult: (result: any) => { return result; },
                    onRefresh: () => {},
                    listeFilters: [{
                        clef: 'mccLibelle',
                        title: this.translateService.instant('opeCarte.liste.filtres.mccLibelle'),
                        type: TypeFilter[TypeFilter.STRING],
                        typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                        isDefault: true
                    },{
                        clef: 'numCarte',
                        title: this.translateService.instant('opeCarte.liste.filtres.carte'),
                        type: TypeFilter[TypeFilter.STRING],
                        typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    },{
                        clef: 'dateVente',
                        title: this.translateService.instant('opeCarte.liste.filtres.dateVente'),
                        type: TypeFilter[TypeFilter.DATE]
                    },{
                        clef: 'montantAchat',
                        title: this.translateService.instant('opeCarte.liste.filtres.montantAchat'),
                        type: TypeFilter[TypeFilter.DECIMAL]
                    },{
                        clef: 'montantReglement',
                        title: this.translateService.instant('opeCarte.liste.filtres.montantReglement'),
                        type: TypeFilter[TypeFilter.DECIMAL]
                    },{
                        clef: 'enseigne',
                        title: this.translateService.instant('opeCarte.liste.filtres.commercant'),
                        type: TypeFilter[TypeFilter.STRING],
                        typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                        isDefault: true
                    },{
                        clef: 'etatOperation',
                        title: this.translateService.instant('opeCarte.liste.filtres.etatOperation'),
                        typeComparaison: TypeComparaison[TypeComparaison.IN],
                        multiple: true,
                        listeObjects: [EtatOperation.NON_JUSTIFIE,EtatOperation.PARTIELLEMENT_JUSTIFIE],
                        listeValues: [
                            {
                                code: EtatOperation.NON_JUSTIFIE,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.nonJustifie')
                            },{
                                code: EtatOperation.PARTIELLEMENT_JUSTIFIE,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.partiellementJustifie')
                            },{
                                code: EtatOperation.JUSTIFIE,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.justifie')
                            },{
                                code: EtatOperation.EN_ATTENTE,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.enAttente')
                            },{
                                code: EtatOperation.DEPENSES_PERSONNELLES,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.depensesPersonnelles')
                            },{
                                code: EtatOperation.ARCHIVES,
                                libelle: this.translateService.instant('opeCarte.liste.etatsCarte.archives')
                            }
                        ],
                        isDefault: false
                    }]
                });

                //Filtre par défaut de la liste
                this.listeOpeCarte.listeSelectedFilters = [{
                    clef: 'etatOperation',
                    title: this.translateService.instant('opeCarte.liste.filtres.etatOperation'),
                    displayedValeur: this.translateService.instant('opeCarte.liste.etatsCarte.nonJustifie') + ', ' + this.translateService.instant('opeCarte.liste.etatsCarte.partiellementJustifie'),
                    typeComparaison: TypeComparaison[TypeComparaison.IN],
                    listeObjects: [EtatOperation.NON_JUSTIFIE,EtatOperation.PARTIELLEMENT_JUSTIFIE],
                    isSelected: true,
                    listeValues: [
                        {
                            code: EtatOperation.NON_JUSTIFIE,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.nonJustifie')
                        },{
                            code: EtatOperation.PARTIELLEMENT_JUSTIFIE,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.partiellementJustifie')
                        },{
                            code: EtatOperation.JUSTIFIE,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.justifie')
                        },{
                            code: EtatOperation.EN_ATTENTE,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.enAttente')
                        },{
                            code: EtatOperation.DEPENSES_PERSONNELLES,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.depensesPersonnelles')
                        },{
                            code: EtatOperation.ARCHIVES,
                            libelle: this.translateService.instant('opeCarte.liste.etatsCarte.archives')
                        }
                    ]
                }
                ];

                //Définition des colonnes de tri
                this.listeOpeCarte.columns = [
                    {key: 'mccLibelle',title: 'opeCarte.liste.filtres.mccLibelle'},
                    {key: 'dateVente',title: 'opeCarte.liste.filtres.dateVente'},
                    {key: 'montantAchat',title: 'opeCarte.liste.filtres.montantAchat'},
                    {key: 'montantReglement',title: 'opeCarte.liste.filtres.montantReglement'},
                    {key: 'enseigne',title: 'opeCarte.liste.filtres.commercant'},
                    {key: 'etatOperation',title: 'opeCarte.liste.filtres.etatOperation'}
                ];

                //Ajout du tri de la liste selon l'ordre voulu
                this.listeOpeCarte.sorting = new Sorting(this.listeOpeCarte.columns,"-dateVente,mccLibelle");

                //Nom de la classe
                this.listeOpeCarte.className = "OpeCarteListComponent";

                //Persistence des filtres
                this.listeOpeCarte.isPersistFilters = true;
            }

            //Bascule du flag pour le rafraichissement
            this.isRefreshNeeded = true;
        });

        //Définition de la liste des actions possibles
        this.listeActions.next([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-archive',
                libelle: 'opeCarte.liste.actions.archiverCarte',
                isVisible: () => this.listeOpeCarte.listeObjetsSectionnes?.length > 0,
                doAction: () => this.archiveOpeCarte(this.userId)
            },
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-rappeler',
                libelle: 'opeCarte.liste.actions.transfererEnAttente',
                isVisible: () => this.listeOpeCarte.listeObjetsSectionnes?.length > 0,
                doAction: () => this.restoreEnAttente(this.userId)
            },
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-emission',
                libelle: 'opeCarte.liste.actions.modifierDepensePersonnelle',
                isVisible: () => this.listeOpeCarte.listeObjetsSectionnes?.length > 0,
                doAction: () => this.saveDepensePersonnelle(this.userId)
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-tout_selectionner',
                libelle: 'liste.actions.selectionnerElementsAffiches',
                isVisible: () => this.listeOpeCarte.listeObjetsSectionnes?.length > 0,
                doAction: () => this.listeOpeCarte.selectAll(true),
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-annuler_selection',
                libelle: 'liste.actions.toutDeselectionner',
                isVisible: () => this.listeOpeCarte.listeObjetsSectionnes?.length > 0,
                doAction: () => this.listeOpeCarte.selectAll(false)
            }
        ]);

    }

    /**
     * Archivage en masse des lots sélectionnés.
     */
    archiveOpeCarte(userId: number): void {
        //S'il y a bien des éléments sélectionnés
        if (this.listeOpeCarte.nbSelectedItems > 0) {
            //Affichage de la popup d'attente
            let waitDialogRef = this.pleaseWaitService.show();

            //Changement de statut des opérations cartes sélectionnées au statut Archivé
            this.opeCarteService.saveArchive(this.listeOpeCarte.listeObjetsSectionnes, this.userId)
                .pipe(first(),finalize(() => {
                    waitDialogRef.close();
                }))
                .subscribe(result => {
                    //Refresh de la liste
                    this.listeOpeCarte.refresh();

                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('opeCarte.liste.message.confirmationTransfertArchive'));
                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                    }
                });
        }
    }

    /**
     * Déplacer en masse des lots sélectionnés vers les dépenses en attente.
     */
    restoreEnAttente(userId: number): void {
        //S'il y a bien des éléments sélectionnés
        if (this.listeOpeCarte.nbSelectedItems > 0) {
            //Affichage de la popup d'attente
            let waitDialogRef = this.pleaseWaitService.show();

            //Changement de statut des opérations cartes sélectionnées au statut En attente
            this.opeCarteService.moveOpCarteEnAttente(this.listeOpeCarte.listeObjetsSectionnes, this.userId)
                .pipe(first(),finalize(() => {
                    waitDialogRef.close();
                }))
                .subscribe(result => {
                    //Refresh de la liste
                    this.listeOpeCarte.refresh();

                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('opeCarte.liste.message.confirmationTransfertEnAttente'));
                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                    }
                });
        }
    }

    /**
     * Déplacer en masse des lots sélectionnés vers les dépenses personnelles.
     */
    saveDepensePersonnelle(userId: number): void {
        //S'il y a bien des éléments sélectionnés
        if (this.listeOpeCarte.nbSelectedItems > 0) {
            //Affichage de la popup d'attente
            let waitDialogRef = this.pleaseWaitService.show();

            //Changement de statut des opérations cartes sélectionnées au statut Dépenses personnelles
            this.opeCarteService.moveOpeCarteDepPerso(this.listeOpeCarte.listeObjetsSectionnes, this.userId)
                .pipe(first(),finalize(() => {
                    waitDialogRef.close();
                }))
                .subscribe(result => {
                    //Refresh de la liste
                    this.listeOpeCarte.refresh();

                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('opeCarte.liste.message.confirmationTransfertDepensePersonnele'));
                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                    }
                });
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation
        this.souscription.unsubscribe();
    }
}
